/**
 * The venue config as ONE shared query (feature 334, research D6).
 *
 * Every card on the board used to read the config with its own 3 s
 * `refetchInterval`: with 40 cards that is 40 timers firing out of phase, each
 * one a request. Now the readers only observe the query, and one timer per venue
 * (shared by however many readers there are) refreshes it every 15 s. Window
 * focus also refreshes it (React Query's default, the data is stale after 15 s).
 * Flags that must reach the screen faster ride the WebSocket instead
 * (`food_block_changed`, `dish_availability_changed`).
 */
import type { QueryClient } from "@tanstack/react-query";

export const CONFIG_REFRESH_MS = 15_000;

export const configQueryKey = (clientId: string | null) => ["kitchen-client-config", clientId] as const;

export function configQueryOptions<T>(
  clientId: string | null,
  fetchConfig: (clientId: string, signal?: AbortSignal) => Promise<T>,
) {
  return {
    queryKey: configQueryKey(clientId),
    queryFn: ({ signal }: { signal?: AbortSignal }) => fetchConfig(clientId!, signal),
    enabled: !!clientId,
    staleTime: CONFIG_REFRESH_MS,
  };
}

interface Refresher {
  refs: number;
  timer: ReturnType<typeof setInterval>;
}

const refreshers = new Map<string, Refresher>();

/** One refresh timer per venue, however many readers ask. Returns the release. */
export function acquireConfigRefresh(
  queryClient: Pick<QueryClient, "refetchQueries">,
  clientId: string | null,
): () => void {
  if (!clientId) return () => undefined;
  let entry = refreshers.get(clientId);
  if (!entry) {
    entry = {
      refs: 0,
      timer: setInterval(() => {
        // cancelRefetch: false -> a read still in flight is left alone.
        void queryClient.refetchQueries(
          { queryKey: configQueryKey(clientId), type: "active" },
          { cancelRefetch: false },
        );
      }, CONFIG_REFRESH_MS),
    };
    refreshers.set(clientId, entry);
  }
  entry.refs += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const current = refreshers.get(clientId);
    if (!current) return;
    current.refs -= 1;
    if (current.refs === 0) {
      clearInterval(current.timer);
      refreshers.delete(clientId);
    }
  };
}
